import React, { useState } from "react";

const DevOps = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn DevOps practices
          to streamline software development and deployment.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          programming, Linux commands, and software development concepts.
          <p className="font-bold text-sm">Tools Needed:</p> Git, Docker,
          Kubernetes, Jenkins, AWS/Azure, Linux, and a code editor (e.g., VS
          Code).
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to DevOps",
          objective: "Understand the fundamentals of DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is DevOps? History and principles.</li>
                <li>DevOps lifecycle: Plan, Build, Test, Deploy, Operate.</li>
                <li>Importance of collaboration and automation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a Linux virtual machine (e.g., Ubuntu).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Linux Basics for DevOps",
          objective: "Learn essential Linux commands for DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>File system navigation and permissions.</li>
                <li>Basic commands: ls, cd, chmod, chown.</li>
                <li>Package management (apt, yum).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice Linux commands on a VM.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Version Control with Git",
          objective: "Understand Git for version control.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Git and GitHub.</li>
                <li>Basic commands: clone, commit, push, pull.</li>
                <li>Branching and merging.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Git repository and push code to GitHub.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Advanced Git Operations",
          objective: "Master advanced Git workflows.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Git stash, rebase, and cherry-pick.</li>
                <li>Resolving merge conflicts.</li>
                <li>Git workflows (e.g., Gitflow).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Resolve a merge conflict in a Git repository.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Introduction to Scripting",
          objective: "Learn scripting for automation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Bash scripting basics.</li>
                <li>Automating repetitive tasks.</li>
                <li>Using variables and loops in Bash.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a Bash script to automate file backups.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Introduction to Docker",
          objective: "Understand containerization with Docker.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are containers? Docker architecture.</li>
                <li>Installing Docker.</li>
                <li>Basic commands: pull, run, stop, remove.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Run a Docker container (e.g., Nginx).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Docker Images and Containers",
          objective: "Learn to build and manage Docker images.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a Dockerfile.</li>
                <li>Building and tagging images.</li>
                <li>Managing containers and volumes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a custom Docker image for a Python app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Docker Compose",
          objective: "Manage multi-container apps with Docker Compose.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Docker Compose.</li>
                <li>Defining services in a docker-compose.yml file.</li>
                <li>Networking and dependencies in Compose.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a multi-container app with Docker Compose.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Introduction to Kubernetes",
          objective: "Understand Kubernetes for container orchestration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Kubernetes? Architecture overview.</li>
                <li>Setting up a local cluster with Minikube.</li>
                <li>Basic concepts: Pods, Nodes, Clusters.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start a Minikube cluster and deploy a pod.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Kubernetes Deployments and Services",
          objective: "Learn Kubernetes deployments and networking.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deployments and ReplicaSets.</li>
                <li>Services and load balancing.</li>
                <li>Kubectl commands for management.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy an app with a Kubernetes Service.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Introduction to CI/CD",
          objective: "Understand Continuous Integration and Deployment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is CI/CD? Benefits and challenges.</li>
                <li>Overview of CI/CD pipelines.</li>
                <li>Tools: Jenkins, GitHub Actions, GitLab CI.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Explore a CI/CD pipeline example.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Setting Up Jenkins",
          objective: "Learn to install and configure Jenkins.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing Jenkins on a Linux machine.</li>
                <li>Configuring Jenkins and plugins.</li>
                <li>Creating a simple Jenkins job.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up Jenkins and run a test job.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Building CI/CD Pipelines with Jenkins",
          objective: "Create a CI/CD pipeline in Jenkins.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Jenkins pipeline syntax.</li>
                <li>Integrating Git with Jenkins.</li>
                <li>Automating build and test stages.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a pipeline to test a Python app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Introduction to Infrastructure as Code (IaC)",
          objective: "Understand IaC concepts and tools.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is IaC? Benefits and principles.</li>
                <li>Tools: Terraform, Ansible.</li>
                <li>Terraform basics: Providers, resources.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Terraform and create a basic configuration.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Provisioning Infrastructure with Terraform",
          objective: "Use Terraform to provision infrastructure.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Writing Terraform scripts.</li>
                <li>Managing state files.</li>
                <li>Deploying resources on a cloud provider.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Provision an AWS EC2 instance with Terraform.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Configuration Management with Ansible",
          objective: "Learn Ansible for configuration management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Ansible.</li>
                <li>Playbooks, roles, and inventory.</li>
                <li>Ansible commands and ad-hoc tasks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write an Ansible playbook to install Nginx.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Introduction to Cloud Platforms",
          objective: "Understand cloud platforms for DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of AWS, Azure, and GCP.</li>
                <li>Key services: EC2, S3, Azure VMs.</li>
                <li>Cloud pricing and free tiers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create an AWS free-tier account and explore services.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Deploying Apps on AWS",
          objective: "Deploy applications on AWS.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using EC2 for app deployment.</li>
                <li>Configuring security groups and SSH access.</li>
                <li>Deploying a Dockerized app on EC2.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a simple app on an EC2 instance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Monitoring and Logging",
          objective: "Learn monitoring and logging in DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of monitoring and logging.</li>
                <li>Tools: Prometheus, Grafana, ELK Stack.</li>
                <li>Setting up basic monitoring.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Prometheus and monitor a local app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Introduction to Grafana",
          objective: "Visualize metrics with Grafana.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Integrating Grafana with Prometheus.</li>
                <li>Creating dashboards and visualizations.</li>
                <li>Setting up alerts.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Grafana dashboard for app metrics.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Introduction to Container Orchestration on Cloud",
          objective: "Deploy apps on Kubernetes in the cloud.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using managed Kubernetes (e.g., EKS, AKS).</li>
                <li>Deploying apps on a cloud Kubernetes cluster.</li>
                <li>Scaling and managing deployments.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy an app on AWS EKS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Introduction to Serverless Computing",
          objective: "Explore serverless architecture.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is serverless? Benefits and use cases.</li>
                <li>AWS Lambda and Azure Functions.</li>
                <li>Deploying a serverless function.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Deploy a simple Lambda function on AWS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Security in DevOps",
          objective: "Learn security best practices in DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>DevSecOps principles.</li>
                <li>Securing CI/CD pipelines.</li>
                <li>Container and cloud security.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Scan a Docker image for vulnerabilities.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Automating Deployments with GitHub Actions",
          objective: "Use GitHub Actions for CI/CD.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to GitHub Actions.</li>
                <li>Creating workflows for CI/CD.</li>
                <li>Automating build, test, and deploy stages.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a GitHub Actions workflow for an app.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Disaster Recovery and High Availability",
          objective: "Learn about disaster recovery in DevOps.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importance of high availability.</li>
                <li>Backup and restore strategies.</li>
                <li>Load balancing and failover.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a backup for an app on AWS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Planning",
          objective: "Plan the final DevOps project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., CI/CD pipeline, app
                  deployment).
                </li>
                <li>Planning infrastructure and tools.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and architecture diagram.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Infrastructure Setup",
          objective: "Set up infrastructure for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Provisioning infrastructure with Terraform.</li>
                <li>Configuring servers with Ansible.</li>
                <li>Setting up a Kubernetes cluster.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Provision project infrastructure on AWS.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – CI/CD Pipeline",
          objective: "Build a CI/CD pipeline for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up a pipeline with Jenkins or GitHub Actions.</li>
                <li>Automating build, test, and deploy stages.</li>
                <li>Integrating Docker and Kubernetes.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the project CI/CD pipeline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Monitoring and Optimization",
          objective: "Add monitoring and optimize the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up monitoring with Prometheus and Grafana.</li>
                <li>Optimizing deployment for scalability.</li>
                <li>Testing high availability.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add monitoring to the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the final DevOps project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo or report.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Implement DevOps practices using modern tools.</li>
            <li>Automate infrastructure provisioning and deployments.</li>
            <li>Build and manage CI/CD pipelines.</li>
            <li>Deploy and monitor applications on cloud platforms.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">CI/CD Pipeline:</span> Build a
              pipeline to deploy a web app.
            </li>
            <li>
              <span className="font-bold text-sm">Kubernetes Deployment:</span>{" "}
              Deploy a microservices app on Kubernetes.
            </li>
            <li>
              <span className="font-bold text-sm">Serverless App:</span> Create
              a serverless application on AWS Lambda.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Encourage practice with real-world scenarios and tools.</li>
            <li>
              Provide resources (e.g., Docker docs, Kubernetes tutorials).
            </li>
            <li>Use discussion time for Q&A and troubleshooting.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from foundational DevOps skills to
            practical deployments over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-green-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">DevOps Syllabus</h1>
        <p className="text-sm text-white">
          Master DevOps Practices (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevOps;
